import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import { useSubscription } from '../../contexts/SubscriptionContext';
import {
  type PaywallCloseReason,
  trackAddonCreditClick,
  trackPaywallClosed,
  trackPaywallPlanSelected,
  trackPaywallViewed,
  trackPlanChanged,
  trackSubscriptionPlanClick,
} from '../../services/analytics.service';
import { getIdToken, isAuthenticated } from '../../services/cognitoService';
import type { PortalError } from '../../services/revenuecatService';
import { openCustomerPortal } from '../../services/revenuecatService';
import logger from '../../utils/logger';
import './Subscription.css';

interface PlanFeature {
  icon: 'users' | 'time' | 'premium' | 'security' | 'support' | 'avatar';
  text: string;
}

interface PricingPlan {
  id: string;
  packageId: 'basic_monthly' | 'plus_monthly' | 'premium_monthly' | null; // RevenueCat package ID
  name: string;
  price: number;
  description: string;
  features: PlanFeature[];
  buttonText: string;
  isCurrent?: boolean;
  isFreeTier?: boolean;
}

const Subscription: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { subscription, credits, purchasePlan, purchaseCredits, isLoading, refreshSubscription } =
    useSubscription();
  const [purchasingId, setPurchasingId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isLoggedIn = isAuthenticated();
  const currentTier = subscription?.tier || 'free';

  const currentTierRef = useRef(currentTier);
  currentTierRef.current = currentTier;
  const creditsRef = useRef(credits?.credits?.total);
  creditsRef.current = credits?.credits?.total;

  // Check if user is returning from checkout or previously initiated a purchase
  const savedAction = typeof window !== 'undefined' ? (sessionStorage.getItem('lastPaywallAction') as PaywallCloseReason | null) : null;
  const savedPlan = typeof window !== 'undefined' ? (sessionStorage.getItem('lastPaywallPlan') || undefined) : undefined;
  const isReturningFromCheckout =
    typeof window !== 'undefined' &&
    (window.location.search.includes('session_id') ||
      window.location.search.includes('success') ||
      Boolean(savedAction));

  const closeReasonRef = useRef<PaywallCloseReason>(
    isReturningFromCheckout && savedAction
      ? savedAction
      : isReturningFromCheckout
        ? 'purchased_subscription'
        : 'not_purchased',
  );
  const lastSelectedPlanRef = useRef<string | undefined>(savedPlan || undefined);
  const hasTrackedViewRef = useRef<boolean>(false);
  const hasTrackedPlanChangeRef = useRef<boolean>(false);

  // Track paywall viewed once on mount and paywall closed on unmount
  useEffect(() => {
    if (!hasTrackedViewRef.current) {
      hasTrackedViewRef.current = true;
      trackPaywallViewed('navbar', currentTierRef.current, creditsRef.current);
    }
    const startTime = Date.now();
    return () => {
      const elapsed = Math.max(0, (Date.now() - startTime) / 1000);
      if (elapsed >= 0.5) {
        trackPaywallClosed({
          reason: closeReasonRef.current || 'not_purchased',
          planViewed: lastSelectedPlanRef.current || currentTierRef.current,
          secondsOnScreen: elapsed,
        });
      }
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('lastPaywallAction');
        sessionStorage.removeItem('lastPaywallPlan');
        sessionStorage.removeItem('previousPlanTier');
      }
    };
  }, []);

  // Track plan_changed when user returns from checkout with upgraded/changed plan
  useEffect(() => {
    if (isLoggedIn && subscription?.tier && subscription.tier !== 'free') {
      const isSuccessReturn =
        window.location.search.includes('session_id') ||
        window.location.search.includes('success=true') ||
        savedAction === 'purchased_subscription';

      if (isSuccessReturn && !hasTrackedPlanChangeRef.current) {
        hasTrackedPlanChangeRef.current = true;
        const prevTier = sessionStorage.getItem('previousPlanTier') || 'free';
        const newTier = savedPlan || subscription.tier;
        if (newTier !== prevTier) {
          trackPlanChanged({
            fromPlan: prevTier,
            toPlan: newTier,
            source: 'subscription_screen',
          });
        }
      }
    }
  }, [isLoggedIn, subscription?.tier, savedAction, savedPlan]);

  // Auto-initialize RevenueCat if user is logged in but subscription data hasn't loaded
  useEffect(() => {
    if (isLoggedIn && !subscription && !isLoading) {
      refreshSubscription();
    }
  }, [isLoggedIn, subscription, isLoading, refreshSubscription]);

  // Reset purchasing state when returning to the page (e.g. Back button from Stripe)
  useEffect(() => {
    const handlePageShow = () => {
      setPurchasingId(null);
    };
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('focus', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('focus', handlePageShow);
    };
  }, []);

  // Plans matching your RevenueCat/Stripe setup
  const plans: PricingPlan[] = [
    {
      id: 'free',
      packageId: null,
      name: t('subscription.plans.free.name', 'Free'),
      price: 0,
      description: t('subscription.plans.free.description', 'Perfect for getting started'),
      features: [
        { icon: 'users', text: t('subscription.features.allAvatars', 'All avatars') },
        {
          icon: 'time',
          text: t('subscription.features.fiveQuestions', '5 text questions per day'),
        },
        { icon: 'avatar', text: t('subscription.features.videoLoop', 'Video loop background') },
      ],
      buttonText:
        isLoggedIn && currentTier === 'free'
          ? t('subscription.currentPlan', 'Current Plan')
          : t('subscription.plans.free.button', 'Get Free Plan'),
      isCurrent: isLoggedIn && currentTier === 'free',
      isFreeTier: true,
    },
    {
      id: 'basic',
      packageId: 'basic_monthly',
      name: t('subscription.plans.basic.name', 'Basic'),
      price: 9.99,
      description: t('subscription.plans.basic.description', 'Perfect for regular chatting'),
      features: [
        {
          icon: 'users',
          text: t('subscription.features.voiceVideoText', 'Live Streaming + Text chat'),
        },
        { icon: 'avatar', text: t('subscription.features.fortyCredits', '40 credits/month') },
        { icon: 'time', text: t('subscription.features.unlimitedText', 'Unlimited text chat') },
      ],
      buttonText:
        isLoggedIn && currentTier === 'basic'
          ? t('subscription.currentPlan', 'Current Plan')
          : isLoggedIn && (currentTier === 'plus' || currentTier === 'premium')
            ? t('subscription.downgrade', 'Downgrade')
            : t('subscription.plans.basic.button', 'Get Basic Plan'),
      isCurrent: isLoggedIn && currentTier === 'basic',
    },
    {
      id: 'plus',
      packageId: 'plus_monthly',
      name: t('subscription.plans.plus.name', 'Plus'),
      price: 19.99,
      description: t('subscription.plans.plus.description', 'Perfect for active chatters'),
      features: [
        {
          icon: 'users',
          text: t('subscription.features.voiceVideoText', 'Live Streaming + Text chat'),
        },
        { icon: 'avatar', text: t('subscription.features.hundredCredits', '100 credits/month') },
        { icon: 'time', text: t('subscription.features.unlimitedText', 'Unlimited text chat') },
      ],
      buttonText:
        isLoggedIn && currentTier === 'plus'
          ? t('subscription.currentPlan', 'Current Plan')
          : isLoggedIn && currentTier === 'premium'
            ? t('subscription.downgrade', 'Downgrade')
            : t('subscription.plans.plus.button', 'Get Plus Plan'),
      isCurrent: isLoggedIn && currentTier === 'plus',
    },
    {
      id: 'premium',
      packageId: 'premium_monthly',
      name: t('subscription.plans.premium.name', 'Premium'),
      price: 29.99,
      description: t('subscription.plans.premium.description', 'Perfect for maximum features'),
      features: [
        {
          icon: 'users',
          text: t('subscription.features.voiceVideoText', 'Live Streaming + Text chat'),
        },
        {
          icon: 'avatar',
          text: t('subscription.features.hundredSixtyCredits', '160 credits/month'),
        },
        { icon: 'time', text: t('subscription.features.unlimitedText', 'Unlimited text chat') },
        { icon: 'support', text: t('subscription.features.prioritySupport', 'Priority support') },
      ],
      buttonText:
        isLoggedIn && currentTier === 'premium'
          ? t('subscription.currentPlan', 'Current Plan')
          : t('subscription.plans.premium.button', 'Get Premium Plan'),
      isCurrent: isLoggedIn && currentTier === 'premium',
    },
  ];

  const handleSelectPlan = async (plan: PricingPlan) => {
    lastSelectedPlanRef.current = plan.id;
    // Track plan selection intent
    await trackSubscriptionPlanClick({
      planId: plan.id,
      planName: plan.name,
      price: plan.price,
      currency: 'USD',
      currentPlan: currentTier,
      actionType: 'subscribe',
      source: 'subscription_screen',
    });
    await trackPaywallPlanSelected({
      planId: plan.id,
      priceTier: plan.id,
    });

    // If not logged in, redirect to register
    if (!isAuthenticated()) {
      closeReasonRef.current = 'redirected_to_login';
      navigate('/register', { state: { from: '/subscription' } });
      return;
    }

    // If it's the current plan or free tier, do nothing
    if (plan.isCurrent || plan.isFreeTier) {
      return;
    }

    // If trying to downgrade, open customer portal
    const tierHierarchy = ['free', 'basic', 'plus', 'premium'];
    const currentTierIndex = tierHierarchy.indexOf(currentTier);
    const targetTierIndex = tierHierarchy.indexOf(plan.id);

    if (currentTierIndex > targetTierIndex && targetTierIndex > 0) {
      try {
        const token = getIdToken();
        await openCustomerPortal(token || undefined);
      } catch (error) {
        logger.error('Subscription', 'Failed to open customer portal', error as Error);
        const portalError = error as PortalError;

        if (
          portalError.message === 'MANAGED_ON_MOBILE' ||
          portalError.platform === 'ios' ||
          portalError.platform === 'android'
        ) {
          const storeName =
            portalError.platform === 'ios' ? 'Apple App Store' : 'Google Play Store';
          setErrorMessage(
            t(
              'subscription.portalMobileErrorExact',
              `Your subscription is managed through the ${storeName}. Please use your device settings to manage it.`,
              { store: storeName },
            ),
          );
        } else {
          setErrorMessage(
            t(
              'subscription.portalError',
              'Unable to open subscription management. Please try again.',
            ),
          );
        }
      }
      return;
    }

    // Start purchase flow
    if (plan.packageId) {
      setErrorMessage(null);
      setPurchasingId(plan.packageId);
      closeReasonRef.current = 'purchased_subscription';
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('lastPaywallAction', 'purchased_subscription');
        sessionStorage.setItem('lastPaywallPlan', plan.id);
        sessionStorage.setItem('previousPlanTier', currentTier);
      }
      try {
        const success = await purchasePlan(plan.packageId);
        if (success) {
          closeReasonRef.current = 'purchased_subscription';
        }
      } catch (error) {
        logger.error('Subscription', 'Purchase error', error as Error);
        setErrorMessage(t('subscription.purchaseError', 'Purchase failed. Please try again.'));
        closeReasonRef.current = 'not_purchased';
        if (typeof window !== 'undefined') {
          sessionStorage.removeItem('lastPaywallAction');
          sessionStorage.removeItem('lastPaywallPlan');
        }
      } finally {
        setPurchasingId(null);
      }
    }
  };

  const handleManageSubscription = async () => {
    try {
      const token = getIdToken();
      await openCustomerPortal(token || undefined);
    } catch (error) {
      logger.error('Subscription', 'Failed to open customer portal', error as Error);
      const portalError = error as PortalError;
      const platform = portalError.platform;

      if (
        portalError.message === 'MANAGED_ON_MOBILE' ||
        platform === 'ios' ||
        platform === 'android'
      ) {
        const storeName = platform === 'ios' ? 'Apple App Store' : 'Google Play Store';
        setErrorMessage(
          t(
            'subscription.portalMobileErrorExact',
            `Your subscription is managed through the ${storeName}. Please use your device settings to manage it.`,
            { store: storeName },
          ),
        );
      } else {
        setErrorMessage(
          t(
            'subscription.portalError',
            'Unable to open subscription management. Please try again.',
          ),
        );
      }
    }
  };

  const renderFeatureIcon = (icon: string, isDark: boolean = false) => {
    const color = isDark ? '#FFFFFF' : '#000000';
    switch (icon) {
      case 'users':
        return (
          <svg
            aria-hidden="true"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="2"
          >
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        );
      case 'time':
        return (
          <svg
            aria-hidden="true"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        );
      case 'premium':
        return (
          <svg
            aria-hidden="true"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="2"
          >
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        );
      case 'security':
        return (
          <svg
            aria-hidden="true"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="2"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
        );
      case 'support':
        return (
          <svg
            aria-hidden="true"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="2"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        );
      case 'avatar':
        return (
          <svg
            aria-hidden="true"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="2"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="subscription-page-wrapper">
      {/* Background */}
      <div className="bg-accent-shape" />

      <div className="subscription-page">
        <Header isLoggedIn={isLoggedIn} />

        <main className="subscription-main">
          {/* Breadcrumb */}
          <nav className="subscription-breadcrumb">
            <button
              type="button"
              className="back-btn"
              onClick={() => navigate(-1)}
              aria-label={t('common.back', 'Back')}
            >
              <svg
                aria-hidden="true"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="breadcrumb-links">
              <Link to="/">{t('header.home', 'Home')}</Link>
              <svg
                aria-hidden="true"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
              <span className="current">{t('header.subscription', 'Subscription')}</span>
            </div>
          </nav>

          {/* Hero Section */}
          <section className="subscription-hero">
            <div className="hero-title-row">
              <span className="hero-line"></span>
              <h2 className="hero-label">{t('header.subscription', 'Subscription')}</h2>
            </div>
            <h1 className="hero-title">{t('subscription.heroTitle', 'TheyChat pricing plans')}</h1>
          </section>

          {/* Pricing Section */}
          <section className="pricing-section">
            <div className="pricing-header">
              <h2 className="pricing-title">
                {t('subscription.pricingTitle', 'Simple pricing plans')}
              </h2>
              <p className="pricing-subtitle">
                {t('subscription.pricingSubtitle', 'Enjoy the power of the TheyChat platform')}
              </p>
            </div>

            {/* Error Banner */}
            {errorMessage && (
              <div className="subscription-error-banner">
                <span>{errorMessage}</span>
                <button
                  type="button"
                  className="error-dismiss-btn"
                  onClick={() => setErrorMessage(null)}
                  aria-label={t('common.dismiss', 'Dismiss')}
                >
                  <svg
                    aria-hidden="true"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            {/* Pricing Cards */}
            <div className="pricing-cards">
              {plans.map((plan) => (
                <div key={plan.id} className={`pricing-card ${plan.isCurrent ? 'current' : ''}`}>
                  <div className="card-content">
                    {/* Price */}
                    <div className="price-row">
                      <span className="price">
                        ${plan.price.toFixed(2)} <span className="currency">USD</span>
                      </span>
                      <span className="price-label">
                        {plan.price > 0 ? t('subscription.perMonth', '/month') : ''}
                      </span>
                    </div>

                    {/* Plan Info */}
                    <div className="plan-info">
                      <h3 className="plan-name">
                        {plan.name}
                        {plan.isCurrent && (
                          <span className="current-badge">
                            {t('subscription.current', 'Current')}
                          </span>
                        )}
                      </h3>
                      <p className="plan-description">{plan.description}</p>
                    </div>

                    {/* Features */}
                    <ul className="features-list">
                      {plan.features.map((feature) => (
                        <li key={`${plan.id}-${feature.text}`} className="feature-item">
                          {renderFeatureIcon(feature.icon, false)}
                          <span>{feature.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA Button */}
                  <button
                    type="button"
                    className={`plan-btn ${plan.isCurrent ? 'current-btn' : ''}`}
                    onClick={() => handleSelectPlan(plan)}
                    disabled={plan.isCurrent || purchasingId !== null || isLoading}
                  >
                    {plan.isCurrent && (
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        aria-hidden="true"
                        style={{ width: '18px', height: '18px', marginRight: '8px' }}
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                    {purchasingId !== null && purchasingId === plan.packageId
                      ? t('subscription.processing', 'Processing...')
                      : plan.buttonText}
                  </button>
                </div>
              ))}
            </div>
          </section>

          {/* Credit Packs Section */}
          <section className="credit-packs-section">
            <div className="pricing-header">
              <h2 className="pricing-title">
                {t('subscription.creditPacks.title', 'Need more credits?')}
              </h2>
              <p className="pricing-subtitle">
                {t(
                  'subscription.creditPacks.subtitle',
                  'Purchase additional credits that never expire',
                )}
              </p>
            </div>

            <div className="credit-packs-grid">
              {[
                { id: 'pack_25', credits: 25, price: 6.99 },
                { id: 'pack_50', credits: 50, price: 12.99 },
                { id: 'pack_75', credits: 75, price: 15.99 },
                { id: 'pack_125', credits: 125, price: 27.99 },
                { id: 'pack_150', credits: 150, price: 29.99 },
                { id: 'pack_250', credits: 250, price: 49.99 },
              ].map((pack) => (
                <div key={pack.id} className="credit-pack-card">
                  <div className="pack-credits">{pack.credits}</div>
                  <div className="pack-label">
                    {t('subscription.creditPacks.credits', 'Credits')}
                  </div>
                  <div className="pack-price">
                    ${pack.price.toFixed(2)} <span className="currency">USD</span>
                  </div>
                  <button
                    type="button"
                    className="pack-btn"
                    onClick={async () => {
                      lastSelectedPlanRef.current = pack.id;
                      closeReasonRef.current = 'purchased_addon_credits';
                      if (typeof window !== 'undefined') {
                        sessionStorage.setItem('lastPaywallAction', 'purchased_addon_credits');
                        sessionStorage.setItem('lastPaywallPlan', pack.id);
                      }
                      await trackAddonCreditClick({
                        packId: pack.id,
                        credits: pack.credits,
                        price: pack.price,
                        currency: 'USD',
                        currentPlan: currentTier,
                        source: 'subscription_screen',
                      });
                      if (!isAuthenticated()) {
                        closeReasonRef.current = 'redirected_to_login';
                        navigate('/register', { state: { from: '/subscription' } });
                        return;
                      }
                      setPurchasingId(pack.id);
                      try {
                        const success = await purchaseCredits(
                          pack.id as
                            | 'pack_25'
                            | 'pack_50'
                            | 'pack_75'
                            | 'pack_125'
                            | 'pack_150'
                            | 'pack_250',
                        );
                        if (success) {
                          closeReasonRef.current = 'purchased_addon_credits';
                        }
                      } catch (error) {
                        logger.error('Subscription', 'Credit purchase error', error as Error);
                        setErrorMessage(
                          t(
                            'subscription.creditPurchaseError',
                            'Purchase failed. Please try again.',
                          ),
                        );
                        closeReasonRef.current = 'not_purchased';
                        if (typeof window !== 'undefined') {
                          sessionStorage.removeItem('lastPaywallAction');
                          sessionStorage.removeItem('lastPaywallPlan');
                        }
                      } finally {
                        setPurchasingId(null);
                      }
                    }}
                    disabled={purchasingId !== null || isLoading}
                  >
                    {purchasingId === pack.id
                      ? t('subscription.processing', 'Processing...')
                      : t('subscription.creditPacks.buy', 'Buy Credits')}
                  </button>
                </div>
              ))}
            </div>
          </section>

          {/* Help Section */}
          <section className="help-section">
            <div className="help-content">
              <h3>{t('subscription.help.title', 'Need help with your subscription?')}</h3>
              <p>
                {t(
                  'subscription.help.text',
                  "If you have any questions about billing or your plan, our support team is here to help you. We'll get back to you as soon as possible.",
                )}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '16px' }}>
              {isLoggedIn && currentTier !== 'free' && (
                <button
                  type="button"
                  className="manage-btn-premium"
                  onClick={handleManageSubscription}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <title>Manage Subscription</title>
                    <path d="M12 2v20M2 12h20" />
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                  {t('subscription.manageSubscription', 'Manage Subscription')}
                </button>
              )}
              <button
                type="button"
                className="contact-btn"
                onClick={() => {
                  window.location.href = 'mailto:support@theychat.ai';
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <title>Contact Support</title>
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                {t('subscription.help.contact', 'Contact Support')}
              </button>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </div>
  );
};

export default Subscription;
